"use client";

import { Amplify } from "aws-amplify";
import { Authenticator, ThemeProvider } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { usePathname } from "next/navigation";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/hooks/useAuth";
import { amplifyConfig, isCognitoConfigured } from "@/lib/amplify-config";
import { thexisTheme } from "@/lib/amplify-theme";
import { isPublicAuthRoute } from "@/lib/route-classes";
import { AuthCard } from "@/components/auth/AuthCard";
import { MarketingTopbar } from "@/components/landing/MarketingTopbar";
import { TrustStrip } from "@/components/landing/TrustStrip";

if (isCognitoConfigured) {
  Amplify.configure(amplifyConfig, { ssr: true });
}

// Public-route classification lives in `lib/route-classes.ts` — single
// source of truth shared with AppShell.tsx (which uses it to pick the
// chrome bucket). Adding a public route there picks it up here too.

// PRE-001 fix (plan-eng-review 2026-04-30): when an unauthenticated
// visitor lands on an APP route (/dashboard, /cases, etc.), we used to
// render Amplify's Authenticator widget bare — no MarketingTopbar, no
// AuthCard framing, no thexis branding. Now this component renders the
// SAME editorial sign-in chrome that /login uses, with the Authenticator
// form inside AuthCard. Once the user signs in (Cognito Hub event →
// AuthContext.syncSession → user populated), it switches to rendering
// the actual app children.
//
// AuthProvider must be ABOVE this component (MarketingTopbar uses
// useAuth) — see the parent <Providers /> below.
function GatedAppRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div
          role="status"
          aria-label="Loading"
          className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/20 border-t-foreground"
        />
      </div>
    );
  }

  if (user) {
    // Authed — render the actual app content. AppShell decides chrome.
    return <>{children}</>;
  }

  // Unauthed — render the editorial sign-in surface. Same chrome as
  // /login but rendered at the Providers layer so any APP route gets
  // the proper UX, not the bare Amplify form.
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <MarketingTopbar variant="auth-login" />
      <main className="flex flex-1 flex-col">
        <AuthCard
          eyebrow="Sign in"
          heading="Welcome back."
          lede="Continue your claim intelligence workspace."
        >
          <ThemeProvider theme={thexisTheme}>
            <Authenticator hideSignUp>{() => <></>}</Authenticator>
          </ThemeProvider>
        </AuthCard>
      </main>
      <TrustStrip />
    </div>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  const skipGate = isPublicAuthRoute(pathname);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {isCognitoConfigured && !skipGate ? (
          <GatedAppRoute>{children}</GatedAppRoute>
        ) : (
          children
        )}
      </AuthProvider>
    </QueryClientProvider>
  );
}
